import { FunctionComponent, MouseEvent } from 'react'

import cx from 'classnames'
import { isEmpty, result } from 'libraries/lodash'
import { CurrentView } from 'front-end-core/services/PageStateService'
import { map, distinctUntilChanged } from 'rxjs/operators'

import { AccordionHeader } from 'services/AccordionService'
import Icon from 'components/shared/Icon'
import useBaseState from 'hooks/useBaseState'

const StickyHeader: FunctionComponent = () => {
  const { baseState } = useBaseState<StickyHeaderBaseState>([
    AccordionHeader.pipe(map(stack => ({ stack }))),
    CurrentView.pipe(distinctUntilChanged(),map((pageView: string) => ({ pageView })))
  ] as any, { stack: [], pageView: 'main', left: 0 })

  return isEmpty(baseState.stack) ? null : <Stack {...{ baseState }} />
}

export default StickyHeader

const Stack:FunctionComponent<{baseState: StickyHeaderBaseState}> = ({ baseState }) => {
  const { pageView, stack } = baseState

  const left: number = result(baseState, ['stack', 0, 'left'])
  const maxWidth: string = result(baseState, ['stack', 0, 'maxWidth'])
  const className = cx({
    'cd-accordion-header': true,
    [`view-${pageView}`]: true
  })

  const handleSelect = (id: string, evt: MouseEvent<HTMLLabelElement>) => {
    const checkboxElement = document.getElementById(id)
    checkboxElement && checkboxElement.click()
    evt.preventDefault()
  }

  const handleClick = (item: {id:string}, evt:MouseEvent<HTMLLabelElement>) => {
    const checkboxElement = document.getElementById(item.id)
    if (checkboxElement) {
      checkboxElement.click()
    }
    evt.preventDefault()
  }

  return (
    <div
      className={className}
      style={{ paddingRight: -2 * left, maxWidth: parseInt(maxWidth) - 2 * left }}
      data-scroll-relay='.scrolling-container'
    >
      <div className='layout-width-constraint cd-list-container'>
        <ul className='cd-accordion-menu' id='scroll-adjust'>
          {stack.map(item => (
            <Item {...{ item, handleSelect, handleClick }} key={item.id} />
          ))}
        </ul>
        <div className='fadeout' />
      </div>
    </div>
  )
}

const Item = (props: ItemProps): JSX.Element => {
  switch (props.item.type) {
    case 'group':
      return <Group {...props} />
    case 'content':
      return <Content {...props} />
    default:
      return null
  }
}

const Group: FunctionComponent<ItemProps> = ({ item, handleSelect, handleClick }) => {
  const { prefix, suffix, id, style = {}, selected, depth, isArchived: _isArchived, expanded } = item
  const Prefix = (prefix && <span className='group-prefix'>{prefix}</span>) || null
  const Suffix = (suffix && <span className='suffix'>{suffix}</span>) || null
  const isArchived = _isArchived === 'true'
  const isSelected = !!+selected
  const itemClass = cx(
    'group pinned',
    `depth-${depth}`,
    { selectable: isSelected }
  )

  return (
    <li style={style} className={itemClass} ref={id}>
      {isSelected && <Selected {...{ id, handleSelect }} />}
      <input type='checkbox' name={id} id={id} checked={+expanded > 0} onChange={() => {}} />
      <label
        className={cx(
          `depth-${depth} group expanded`,
          { 'group-header-archived': isArchived }
        )}
        htmlFor={id + '/0'}
        onClick={(evt) => handleClick(item, evt)}
      >
        <span className='before'><Icon name='icon-chevron-down' /></span>
        {Prefix}
        {Suffix}
        <span dangerouslySetInnerHTML={{ __html: item.title }} />
        {isArchived && <span title='Archived' className='archived-label'>A</span>}
      </label>
    </li>
  )
}

const Selected: FunctionComponent<SelectedProps> = ({ id, handleSelect }) => {
  const inputId = id + 'sel/0'
  const prefixClass = cx('selected', { selector: true })

  return (
    <label
      htmlFor={inputId}
      className={prefixClass}
      onClick={(evt) => handleSelect(inputId, evt)}
      data-help-item='accordionselector'
      data-help-position='left'
    >
      <span className='indicator'>
        <Icon name={{ 'icon-checkbox-marked-outline': true }} />
      </span>
    </label>
  )
}

const Content: FunctionComponent<ItemProps> = ({ item, handleSelect, handleClick }) => {
  const { title, depth, prefix, id, selected, expanded, style = {} } = item
  const isSelected = !!+selected
  const Prefix = <span className='prefix'>{prefix}</span>
  const inputId = id + 'sel/0'
  let Selected

  if (isSelected) {
    Selected = (
      <label
        htmlFor={inputId}
        className={cx('selector', { selected: isSelected })}
        onClick={(evt) => handleSelect(inputId, evt)}
      >
        <span className='indicator'>
          <Icon name={{ 'icon-checkbox-blank-outline': !isSelected, 'icon-checkbox-marked-outline': isSelected }} />
        </span>
      </label>
    )
  }

  const itemClass = cx({
    content: true,
    [`depth-${depth}`]: true,
    selectable: isSelected
  })

  return (
    <li style={style} className={itemClass} ref={id} key={id}>
      {Selected}
      {Prefix}
      <input type='checkbox' name={id} id={id} checked={+expanded > 0} onChange={() => {}} />
      <label className={`depth-${depth} content`} onClick={(evt) => handleClick(item, evt)}>
        <span dangerouslySetInnerHTML={{ __html: title }} />
      </label>
    </li>
  )
}

interface StickyHeaderBaseState {
  stack: StackItem[];
  pageView:string;
  left: number
};

interface ItemProps {
  item: StackItem;
  handleSelect: (item: string, evt:MouseEvent<HTMLLabelElement>) => void;
  handleClick: (item: StackItem, evt:MouseEvent<HTMLLabelElement>) => void;
}

interface StackItem {
  type: 'content'| 'group';
  id: string;
  title: string;
  depth: number;
  prefix: string;
  suffix: string;
  selected:boolean;
  expanded: boolean;
  isArchived: string;
  style: any;
}

interface SelectedProps extends Pick<ItemProps, 'handleSelect'>{
  id: string;
}
